import { CurrentPlan } from '@dgx-pr-spa/shared/util';
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { filter, switchMap, map } from 'rxjs/operators';
import * as fromOffer from '../store/actions';
import * as fromOfferQuery from '../store/selectors';
import * as fromOfferState from '../store/reducers';
import { of } from 'rxjs';
import {
  isUpsellExclusionActive,
  planType,
} from '@shared/helpers/function.helpers';
import { Offer } from '@dgx-pr-spa/shared/util';
import { BuildConfigService } from '@dgx-pr-spa/ui-components';
import {
  GAModalClose,
  GAModalLanding,
  GAModalNavigate,
} from '@analytics/classes/ga.modal.class';
import { AnalyticsService } from '@analytics/services/analytics.service';
import { PlanType, UpsellExclusions } from '@dgx-pr-spa/shared/util';

@Injectable()
export class LandingModalService {
  constructor(
    public store: Store<fromOfferState.ViewOffersState>,
    public configService: BuildConfigService,
    public analyticsService: AnalyticsService
  ) {}

  readonly offers$ = this.store.select(fromOfferQuery.getOffers);
  readonly offersLoaded$ = this.store.select(fromOfferQuery.getOffersLoaded);
  readonly landingState$ = this.store.select(
    fromOfferQuery.getLandingViewedState
  );

  readonly showLandingModal$ = this.offersLoaded$.pipe(
    filter((loaded) => loaded),
    switchMap(() => this.landingState$),
    switchMap((landingModalViewed: boolean) => {
      const { landingModalEnabled } = this.configService.config;
      return of(landingModalEnabled && !landingModalViewed);
    })
  );

  readonly getSlides$ = this.offers$.pipe(
    map((offers) => this.getSlideContent(offers?.current_plan, offers?.offer))
  );

  gaLanding() {
    const gaData = new GAModalLanding().gaData;
    this.analyticsService.eventEmitter(gaData);
  }

  gaSlideNavigation(slide: number) {
    const gaData = new GAModalNavigate(slide).gaData;
    this.analyticsService.eventEmitter(gaData);
  }

  gaClose(type: string) {
    const gaData = new GAModalClose(type).gaData;
    this.analyticsService.eventEmitter(gaData);
  }

  setModalViewed(type: string) {
    this.store.dispatch(fromOffer.viewedLandingModal());
    this.gaClose(type);
  }

  getSlideContent(currentPlan: CurrentPlan, offer: Offer) {
    const slides = [
      this.configService?.config?.isCovidNoticeEnabled
        ? {
            title: `An update from us during COVID-19`,
            text: `We're currently experiencing some delays with our services. The safety of our customers and engineers is our top priority. Look out for updates in our Covid-19 FAQs.`,
            url: './assets/images/landing-modal-steps/step-covid-updates.png',
          }
        : null,
      {
        title: 'Recycle and installation',
        text: this.configService?.config?.isCovidNoticeEnabled
          ? `Some suppliers are not currently offering these services. If you're offered installation, it can be added to your replacement order. For recycle or disposal, you'll need to have your old appliance ready for collection.`
          : `You'll be able to choose if you want us to install your new appliance or recycle your old one. If you select recycle, you'll be sent information on how to prepare your appliance for removal.`,
        url: './assets/images/landing-modal-steps/step-1-v2.png',
      },
      {
        title: `Choosing your new appliance`,
        text: `You'll be able to choose a new appliance from a variety that have similar specifications to your current one. You'll also have the chance to upgrade to an enhanced model.`,
        url: './assets/images/landing-modal-steps/step-2-v2.png',
      },
      {
        title: 'Appliance availability',
        text: `Sometimes we can't offer a replacement that's an exact match of your current appliance. This may be due to a lack of availability or model discontinuation. However, you'll always be offered a model with the same or similar specifications as your current one.`,
        url: './assets/images/landing-modal-steps/step-3-v2.png',
      },

      planType(currentPlan) === PlanType.SINGLE &&
      offer?.upsell_reason !== UpsellExclusions.UPSELL_DISABLED
        ? {
            title: `New appliance, new policy`,
            text: `Your current plan has ended. But don’t worry, your new appliance will be covered when you buy a new policy.`,
            url: './assets/images/landing-modal-steps/step-4-v2.png',
          }
        : isUpsellExclusionActive(offer) &&
          planType(currentPlan) === PlanType.MULTIPLAN &&
          offer?.upsell_reason !== UpsellExclusions.UPSELL_DISABLED
        ? {
            title: `Your new appliance is covered`,
            text: `There’s no need to worry about a new plan for your new appliance. Your current plan will automatically cover it.`,
            url: './assets/images/landing-modal-steps/step-4-v2.png',
          }
        : null,

      {
        title: `We've got your back!`,
        text: `There's no need to register your new appliance, we'll do it for you once you've chosen it.`,
        url: './assets/images/landing-modal-steps/step-5-v2.png',
      },
    ];

    return slides.filter(Boolean);
  }
}
